import I18n from 'i18n-js';
import { reject } from 'lodash';
import React from 'react';

import { V3 } from '../api_request';
import UrlHelpers from '../shared/url_helpers';
import bind from '@reverbdotcom/commons/src/bind';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import LoadingBars from '@reverbdotcom/commons/src/components/loading_bars';
import FormSection from '@reverbdotcom/commons/src/components/form_section';
import RateOptionsToggles from './rate_options_toggles';
import { ShippingRate, IShippingRate } from './shipping_rate';
import { ShippingRegionSelector, IShippingRegion } from '@reverbdotcom/commons/src/shipping/shipping_region_selector';

interface IProps {
  shippingRates: IShippingRate[];
  currency: string;
  allowedToShipExpedited: boolean;
  isExpeditedShippingEnabled: boolean;
  allowedToShipCombined: boolean;
  isCombinedShippingEnabled: boolean;
}

interface IState {
  shippingRates: IShippingRate[];
  isLoaded: boolean;
  isExpeditedShippingEnabled: boolean;
  isCombinedShippingEnabled: boolean;
  showNewShippingRegion: boolean;
  shippingRegions: IShippingRegion[];
  newShippingRegion: IShippingRate;
}

class ShippingRatesEditor extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: false,
      isExpeditedShippingEnabled: props.allowedToShipExpedited && props.isExpeditedShippingEnabled,
      isCombinedShippingEnabled: props.allowedToShipCombined && props.isCombinedShippingEnabled,
      shippingRates: props.shippingRates,
      showNewShippingRegion: false,
      shippingRegions: [],
      newShippingRegion: {},
    };
  }

  componentDidMount() {
    this.fetchShippingRegions();
  }

  @bind
  handleNewShippingRegionChange(newShippingRegion) {
    if (!newShippingRegion.regionCode) { return null; }

    return this.setState({ newShippingRegion });
  }

  @bind
  handleAddBlankRateForNewRegion(event) {
    event.preventDefault();

    this.setState((prevState) => {
      if (!prevState.newShippingRegion.regionCode) { return prevState; }

      const { regionType, regionCode, regionName } = prevState.newShippingRegion;

      const newBlankRate = {
        isRequired: false,
        regionType,
        regionCode,
        regionName,
        rate: {
          amount: '',
          currency: this.props.currency,
        },
        expeditedRate: {
          amount: '',
          currency: this.props.currency,
        },
        incrementalRate: {
          amount: '',
          currency: this.props.currency,
        },
      };

      return {
        ...prevState,
        shippingRates: prevState.shippingRates.slice().concat(newBlankRate),
        newShippingRegion: {},
      };
    });
  }

  @bind
  handleRemoveShippingRate(shippingRateToRemove) {
    const shippingRates = reject(this.state.shippingRates, shippingRate =>
      shippingRate.regionType === shippingRateToRemove.regionType &&
        shippingRate.regionCode === shippingRateToRemove.regionCode);

    this.setState({ shippingRates });
  }

  @bind
  toggleExpeditedShipping() {
    this.setState((prevState) => ({ isExpeditedShippingEnabled: !prevState.isExpeditedShippingEnabled }));
  }

  @bind
  toggleCombinedShipping() {
    this.setState((prevState) => ({ isCombinedShippingEnabled: !prevState.isCombinedShippingEnabled }));
  }

  @bind
  toggleAddRegionFormVisible() {
    this.setState((prevState) => ({ showNewShippingRegion: !prevState.showNewShippingRegion }));
  }

  fetchShippingRegions() {
    V3.get(UrlHelpers.shippingRegionsPath).done((response) => {
      this.setState({
        shippingRegions: response.shipping_regions,
        isLoaded: true,
      });
    });
  }

  currentlyActiveRegions() {
    return this.state.shippingRates.map(({ regionCode }) => regionCode);
  }

  renderNewShippingRegionDropdown() {
    if (this.state.showNewShippingRegion) {
      return (
        <>
          <ShippingRegionSelector
            shippingRegions={this.state.shippingRegions}
            onSelect={this.handleNewShippingRegionChange}
            disabledShippingRegionCodes={this.currentlyActiveRegions()}
            value={this.state.newShippingRegion.regionCode}
          />
          <button
            type="button"
            className="button button--blue"
            onClick={this.handleAddBlankRateForNewRegion}
            disabled={!this.state.newShippingRegion.regionCode}
          >
            {I18n.t('discovery.dashboard.selling.shippingRates.addRate')}
          </button>
          <button
            type="button"
            className="button-as-link ml-2"
            onClick={this.toggleAddRegionFormVisible}
          >
            {I18n.t('discovery.dashboard.selling.shippingRates.cancelAddRate')}
          </button>
        </>
      );
    }

    return (
      <div className="scaling-mt-2">
        <button
          className="button button--blue button--small icon-r-plus"
          type="button"
          onClick={this.toggleAddRegionFormVisible}
          aria-label={I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocation')}
        >
          {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocation')}
        </button>
        <p className="mt-1 size-90 lh-120 mb-0">
          {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocationExplainer')}
          <CoreLink
            to={UrlHelpers.internationalShippingGuidePath}
            target="_blank"
            className="ml-space"
          >
            {I18n.t('discovery.dashboard.selling.shippingRates.toggles.learnMore')}
          </CoreLink>
        </p>
      </div>
    );
  }

  render() {
    if (!this.state.isLoaded) {
      return (
        <div className="scaling-padding-2 d-flex fx-justify-center bg-white">
          <LoadingBars size="large" />
        </div>
      );
    }

    return (
      <div className="form-sections">
        <FormSection
          title={I18n.t('discovery.dashboard.selling.shippingRates.rateOptionsTitle')}
          description={(
            <p className="mb-0">
              {I18n.t('discovery.dashboard.selling.shippingRates.rateOptionsDescription')}
            </p>
          )}
        >
          <RateOptionsToggles
            allowedToShipExpedited={this.props.allowedToShipExpedited}
            allowedToShipCombined={this.props.allowedToShipCombined}
            isExpeditedShippingEnabled={this.state.isExpeditedShippingEnabled}
            isCombinedShippingEnabled={this.state.isCombinedShippingEnabled}
            handleExpeditedShippingToggle={this.toggleExpeditedShipping}
            handleCombinedShippingToggle={this.toggleCombinedShipping}
            currency={this.props.currency}
          />
        </FormSection>

        <FormSection
          title={I18n.t('discovery.dashboard.selling.shippingRates.shippingRatesTitle')}
          description={(
            <p className="mb-0">
              {I18n.t('discovery.dashboard.selling.shippingRates.shippingRatesDescription')}
            </p>
          )}
        >
          <div className="shipping-rate-cards">
            {this.state.shippingRates.map((shippingRate) => (
              <div
                className="scaling-mb-2"
                key={shippingRate.regionCode}
              >
                <ShippingRate
                  isExpeditedShippingEnabled={this.state.isExpeditedShippingEnabled}
                  isCombinedShippingEnabled={this.state.isCombinedShippingEnabled}
                  shippingRate={shippingRate}
                  handleRemoveShippingRate={this.handleRemoveShippingRate}
                />
              </div>
            ))}
            {this.renderNewShippingRegionDropdown()}
          </div>
        </FormSection>
      </div>
    );
  }
}

export default ShippingRatesEditor;
